import {
  CalculateBatchPayments,
  CommissionsBatchFilterPageRequest,
  CommissionsBatchOutputFilterPageRequest,
  CommissionsFilterPageRequest,
  ManualBailAccountMovementsFilterPageRequest,
} from "../../../../modules/commissions/batch/types";
import { ManualDebtAccountMovementsFilterPageRequest } from "../../../../modules/commissions/debtaccount/types";
import { CommissionsSettingsLevelFilterPageRequest } from "../../../../modules/commissions/level/types";
import {
  CalculatedCommissionsFilterPageRequest,
  CommissionsOutputFilterPageRequest,
} from "../../../../modules/commissions/outputs/types";
import { PostponedCommissionsFilterPageRequest } from "../../../../modules/commissions/postponed/types";
import { BusinessCoefficientsReportFilterPageRequest } from "../../../../modules/commissions/productcommissionssettings/types";
import { CommissionPromotionFilterPageRequest } from "../../../../modules/commissions/promotions/types";
import {
  SpecialCommissionsFilterPageRequest,
  SpecialCommissionsReportFilterPageRequest,
} from "../../../../modules/commissions/special/types";
import type { SplitCommissionsFilterPageRequest } from "../../../../modules/commissions/splitcommissions/types";
import type { UUID } from "../../../../typings/global";
import { EntityObject, PageRequest, SearchPageRequest, TwoLevelEntityObject } from "../../../types";

export const COMMISSION_PROMOTIONS_QUERY_KEYS = {
  filterCommissionPromotions: (payload?: CommissionPromotionFilterPageRequest) =>
    payload ? ["filterCommissionPromotions", payload] : ["filterCommissionPromotions"],
  approveAllPendingCommissionPromotions: () => ["approveAllPendingCommissionPromotions"],
  updateCommissionPromotion: () => ["updateCommissionPromotion"],
  deleteCommissionPromotion: () => ["deleteCommissionPromotion"],
  downloadCommissionPromotionsExport: () => ["downloadCommissionPromotionsExport"],
};

export const COMMISSIONS_BATCH_QUERY_KEYS = {
  filterCommissionsBatches: (filter?: CommissionsBatchFilterPageRequest) =>
    filter ? ["filterCommissionsBatches", filter] : ["filterCommissionsBatches"],
  getCommissionsBatch: (id?: UUID) => (id ? ["getCommissionsBatch", id] : ["getCommissionsBatch"]),
  getCommissionsBatchNamePrefix: () => ["getCommissionsBatchNamePrefix"],
  updateCommissionsBatch: () => ["updateCommissionsBatch"],
  changeCommissionsBatchStep: () => ["changeCommissionsBatchStep"],
  downloadCommissionsBatchOutputsAsZip: () => ["downloadCommissionsBatchOutputsAsZip"],
  downloadCommissionsBatchOutputsExport: () => ["downloadCommissionsBatchOutputsExport"],
  downloadCommissionsBatchInputsExport: () => ["downloadCommissionsBatchInputsExport"],
  downloadCommissionsBatchInactiveAgentsExport: () => ["downloadCommissionsBatchInactiveAgentsExport"],
  updateCommissionsBatchFinishedDate: () => ["updateCommissionsBatchFinishedDate"],
  importSpecialCommissions: () => ["importSpecialCommissions"],
  createCommissionsBatch: () => ["createCommissionsBatch"],
  deleteCommissionsBatch: () => ["deleteCommissionsBatch"],
  createCommissionsUnit: () => ["createCommissionsUnit"],
  updateCommissionsUnit: () => ["updateCommissionsUnit"],
  deleteCommissionsUnit: () => ["deleteCommissionsUnit"],
  uploadCommissionsBatchInput: () => ["uploadCommissionsBatchInput"],
  replaceCommissionsBatchInput: () => ["replaceCommissionsBatchInput"],
  deleteCommissionsBatchInput: () => ["deleteCommissionsBatchInput"],
  filterManualBailAccountMovements: (payload?: EntityObject<ManualBailAccountMovementsFilterPageRequest>) =>
    payload ? ["filterManualBailAccountMovements", payload] : ["filterManualBailAccountMovements"],
  associateBailAccountMovementToBatch: () => ["associateBailAccountMovementToBatch"],
  disassociateBailAccountMovementFromBatch: () => ["disassociateBailAccountMovementFromBatch"],
  filterManualDebtAccountMovements: (payload?: EntityObject<ManualDebtAccountMovementsFilterPageRequest>) =>
    payload ? ["filterManualDebtAccountMovements", payload] : ["filterManualDebtAccountMovements"],
  associateManualDebtAccountMovementToBatch: () => ["associateManualDebtAccountMovementToBatch"],
  disassociateManualDebtAccountMovementFromBatch: () => ["disassociateManualDebtAccountMovementFromBatch"],
  filterCommissionsBatchOutputs: (payload?: EntityObject<CommissionsBatchOutputFilterPageRequest>) =>
    payload ? ["filterCommissionsBatchOutputs", payload] : ["filterCommissionsBatchOutputs"],
  calculateCommissionsBatchPayments: (payload: EntityObject<CalculateBatchPayments>) => [
    "calculateCommissionsBatchPayments",
    payload,
  ],
  generateCommissionsBatchPaymentOrder: () => ["generateCommissionsBatchPaymentOrder"],
  filterCommissions: (payload?: EntityObject<CommissionsFilterPageRequest>) =>
    payload ? ["filterCommissions", payload] : ["filterCommissions"],
  createCommission: () => ["createCommission"],
  updateCommission: () => ["updateCommission"],
  postponeCommission: () => ["postponeCommission"],
  tryToIncludeCommission: () => ["tryToIncludeCommission"],
  recalculateCommissionsForContract: () => ["recalculateCommissionsForContract"],
  updateCalculatedCommissions: () => ["updateCalculatedCommissions"],
  deleteCommission: () => ["deleteCommission"],
  filterSpecialCommissions: (payload?: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>) =>
    payload ? ["filterSpecialCommissions", payload] : ["filterSpecialCommissions"],
  excludeSpecialCommission: () => ["excludeSpecialCommission"],
  includeSpecialCommission: () => ["includeSpecialCommission"],
};

export const COMMISSIONS_OUTPUT_QUERY_KEYS = {
  filterCommissionsOutputs: (payload?: CommissionsOutputFilterPageRequest) =>
    payload ? ["filterCommissionsOutputs", payload] : ["filterCommissionsOutputs"],
  downloadCommissionsOutputsExport: () => ["downloadCommissionsOutputsExport"],
  filterAgentCommissionsOutputs: (payload?: EntityObject<PageRequest>) =>
    payload ? ["filterAgentCommissionsOutputs", payload] : ["filterAgentCommissionsOutputs"],
  createAgentCommissionsOutput: () => ["createAgentCommissionsOutput"],
  updateAgentCommissionsOutput: () => ["updateAgentCommissionsOutput"],
  togglePaidAgentCommissionsOutput: () => ["togglePaidAgentCommissionsOutput"],
  deleteAgentCommissionsOutput: () => ["deleteAgentCommissionsOutput"],
  filterCalculatedCommissionsForAgentOutputs: (
    payload: TwoLevelEntityObject<CalculatedCommissionsFilterPageRequest>,
  ) => ["filterCalculatedCommissionsForAgentOutputs", payload],
  downloadCalculatedCommissionsExportForAgentOutputs: () => ["downloadCalculatedCommissionsExportForAgentOutputs"],
  filterSpecialCommissionsForAgentOutputs: (payload: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>) => [
    "filterSpecialCommissionsForAgentOutputs",
    payload,
  ],
};

export const COMMISSIONS_POSTPONED_QUERY_KEYS = {
  filterPostponedCommissions: (payload?: PostponedCommissionsFilterPageRequest) =>
    payload ? ["filterPostponedCommissions", payload] : ["filterPostponedCommissions"],
  updatePostponedCommission: () => ["updatePostponedCommission"],
  resolvePostponedCommission: () => ["resolvePostponedCommission"],
  unresolvePostponedCommission: () => ["unresolvePostponedCommission"],
  createDuplicatedCommission: () => ["createDuplicatedCommission"],
  updateDuplicatedCommission: () => ["updateDuplicatedCommission"],
  deleteDuplicatedCommission: () => ["deleteDuplicatedCommission"],
  downloadPostponedCommissionsExport: () => ["downloadPostponedCommissionsExport"],
};

export const COMMISSIONS_SETTINGS_QUERY_KEYS = {
  getCommissionsSettings: (id: UUID) => ["getCommissionsSettings", id],
  getProfileCommissionsSettings: (id: UUID) => ["getProfileCommissionsSettings", id],
  assignCommissionsSettingsLevel: () => ["assignCommissionsSettingsLevel"],
  unassignCommissionsSettingsLevel: () => ["unassignCommissionsSettingsLevel"],
  getAgentCommissionsSettingsRankIntervals: (id: UUID) => ["getAgentCommissionsSettingsRankIntervals", id],
  updateAgentCommissionsSettingsRankIntervals: () => ["updateAgentCommissionsSettingsRankIntervals"],
  togglePrimaryPaymentInclusionCommissionsSettings: () => ["togglePrimaryPaymentInclusionCommissionsSettings"],
  toggleSecondaryPaymentInclusionCommissionsSettings: () => ["toggleSecondaryPaymentInclusionCommissionsSettings"],
  toggleUsageAsHeadCommissionsSettings: () => ["toggleUsageAsHeadCommissionsSettings"],
  togglePointsUnificationCommissionsSettings: () => ["togglePointsUnificationCommissionsSettings"],
  togglePrimaryManagerialCommissions: () => ["togglePrimaryManagerialCommissions"],
  toggleSecondaryManagerialCommissions: () => ["toggleSecondaryManagerialCommissions"],
  togglePromotionAllowanceCommissionsSettings: () => ["togglePromotionAllowanceCommissionsSettings"],
  updateInitialPointsCommissionsSettings: () => ["updateInitialPointsCommissionsSettings"],
  updateStatusCommissionsSettings: () => ["updateStatusCommissionsSettings"],
  createCommissionsSettingsRule: () => ["createCommissionsSettingsRule"],
  updateCommissionsSettingsRule: () => ["updateCommissionsSettingsRule"],
  deleteCommissionsSettingsRule: () => ["deleteCommissionsSettingsRule"],
  uploadCommissionsSettingsAttachments: () => ["uploadCommissionsSettingsAttachments"],
  deleteCommissionsSettingsAttachments: () => ["deleteCommissionsSettingsAttachments"],
};

export const COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS = {
  filterCommissionsSettingsLevels: (payload?: CommissionsSettingsLevelFilterPageRequest) =>
    payload ? ["filterCommissionsSettingsLevels", payload] : ["filterCommissionsSettingsLevels"],
  getCommissionsSettingsLevel: (id: string) => ["getCommissionsSettingsLevel", id],
  createCommissionsSettingsLevel: () => ["createCommissionsSettingsLevel"],
  updateCommissionsSettingsLevel: () => ["updateCommissionsSettingsLevel"],
  deleteCommissionsSettingsLevel: () => ["deleteCommissionsSettingsLevel"],
  createCommissionsLevelRule: () => ["createCommissionsLevelRule"],
  updateCommissionsLevelRule: () => ["updateCommissionsLevelRule"],
  deleteCommissionsLevelRule: () => ["deleteCommissionsLevelRule"],
  createCommissionsLevelBailAccountSettings: () => ["createCommissionsLevelBailAccountSettings"],
  updateCommissionsLevelBailAccountSettings: () => ["updateCommissionsLevelBailAccountSettings"],
  deleteCommissionsLevelBailAccountSettings: () => ["deleteCommissionsLevelBailAccountSettings"],
  uploadCommissionsLevelAttachments: () => ["uploadCommissionsLevelAttachments"],
  deleteCommissionsLevelAttachment: () => ["deleteCommissionsLevelAttachment"],
};

export const COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS = {
  filterCommissionsSettingsRanks: (filter?: SearchPageRequest) =>
    filter ? ["filterCommissionsSettingsRanks", filter] : ["filterCommissionsSettingsRanks"],
  createCommissionsSettingsRanks: () => ["createCommissionsSettingsRanks"],
  updateCommissionsSettingsRanks: () => ["updateCommissionsSettingsRanks"],
  deleteCommissionsSettingsRanks: () => ["deleteCommissionsSettingsRanks"],
};

export const SPECIAL_COMMISSIONS_QUERY_KEYS = {
  createSpecialCommissionsRule: () => ["createSpecialCommissionsRule"],
  updateSpecialCommissionsRule: () => ["updateSpecialCommissionsRule"],
  deleteSpecialCommissionsRule: () => ["deleteSpecialCommissionsRule"],
  getSpecialCommissionsReport: (payload: SpecialCommissionsReportFilterPageRequest) => [
    "getSpecialCommissionsReport",
    payload,
  ],
  downloadSpecialCommissionsExport: () => ["downloadSpecialCommissionsExport"],
};

export const SPLIT_COMMISSIONS_QUERY_KEYS = {
  filterSplitCommissions: (payload?: SplitCommissionsFilterPageRequest) =>
    payload ? ["filterSplitCommissions", payload] : ["filterSplitCommissions"],
  downloadSplitCommissionsExport: () => ["downloadSplitCommissionsExport"],
  updateSplitCommissions: () => ["updateSplitCommissions"],
  updateSplitCalculatedCommissions: () => ["updateSplitCalculatedCommissions"],
};

export const PRODUCT_COMMISSIONS_SETTINGS_QUERY_KEYS = {
  createUpdateProductCommissionsSettings: () => ["createUpdateProductCommissionsSettings"],
  getProductBusinessCoefficients: () => ["getProductBusinessCoefficients"],
  filterBusinessCoefficientsReport: (payload?: BusinessCoefficientsReportFilterPageRequest) =>
    payload ? ["filterBusinessCoefficientsReport", payload] : ["filterBusinessCoefficientsReport"],
  downloadBusinessCoefficientsExport: () => ["downloadBusinessCoefficientsExport"],
};

export const PREDICTED_COMMISSIONS_QUERY_KEYS = {
  recalculatePredictedCommissions: () => ["recalculatePredictedCommissions"],
};

export const COMMISSION_ADVANCE_QUERY_KEYS = {
  getCommissionAdvance: (contractId: UUID) => ["getCommissionAdvance", contractId],
  createCommissionAdvance: () => ["createCommissionAdvance"],
  updateCommissionAdvance: () => ["updateCommissionAdvance"],
  createCommissionAdvanceCancellation: () => ["createCommissionAdvanceCancellation"],
  deleteCommissionAdvanceCancellation: () => ["deleteCommissionAdvanceCancellation"],
};
